import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputLabel,
  MenuItem, Select, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { AppDispatch, RootState } from '../reduxStore/Store';
import {
  appliquerFamilleMasse, appliquerPartageMasse, appliquerPrixMasse, appliquerValeursMasse, clearApercuMasse,
  clearMessagesMasse, fetchApercuFamilleMasse, fetchApercuPartageMasse, fetchApercuPrixMasse, fetchApercuValeursMasse,
  appliquerFournisseurMasse, fetchApercuFournisseurMasse,
} from '../reduxStore/ArticleSlice';
import {
  ArticleMasseApercuLigne, ArticleMasseFamilleLigne, ArticleMasseFamilleParams,
  ArticleMasseFournisseurLigne, ArticleMasseFournisseurParams, Tiers,
  ArticleMassePartageLigne, ArticleMassePartageParams, ArticleMassePrixParams,
  ArticleMasseValeursLigne, ArticleMasseValeursParams, Departement, FamilleData,
} from '../models/models';
import {
  ChampDefautFamille, ChampPrix, GeneriqueDataType, ModeAjustement, ModePartage, NiveauPrix, PERMISSION_PRIX_DEPT,
  PERMISSION_PRIX_REVIENT,
} from '../constants/enums';
import { usePermission } from '../hooks/usePermission';
import SelectDepartementsPartage from '../components/Selects/SelectDepartementsPartage';
import FamilleDefautDialog from '../components/Forms/FamilleDefautDialog';
import { fetchAllowedDepartements } from '../reduxStore/ScopeSlice';
import { fetchDepartements } from '../reduxStore/DepartementSlice';
import { SANS_FAMILLE, SANS_FOURNISSEUR } from '../constants/filtres';
import { labels } from '../constants/labels';
import { CHAMP_DEFAUT_LABEL, CHAMP_LABEL, MODE_LABEL, MODE_PARTAGE_LABEL } from '../constants/articleMasseLabels';
import FournisseurAutoC from '../components/AutoComplete/FournisseurAutoC';
import { TableHeader } from '../constants/interfaces';
import { getLabels } from '../constants/fieldLabels';
import MyTable from '../components/MyTable';
import FamilleAutoC from '../components/AutoComplete/FamilleAutoC';
import SelectDepartement from '../components/Selects/SelectDepartement';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppFilterGrid from '../components/layout/AppFilterGrid';
import { useSnackbar } from '../components/SnackbarProvider';

type Action = 'PRIX' | 'FAMILLE' | 'FOURNISSEUR' | 'PARTAGE' | 'VALEURS';

type ParamsAction =
  ArticleMassePrixParams | ArticleMasseFamilleParams | ArticleMasseFournisseurParams
  | ArticleMassePartageParams | ArticleMasseValeursParams;

const ARRONDIS = [1, 5, 10, 25, 50, 100, 500, 1000];

const fmt = (n: number) => Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

/**
 * Actions de masse par famille (migration 0134) — ajuster les prix, changer de famille.
 * Cible = une famille (± sous-familles) ou « sans famille ». L'aperçu et
 * l'application sont calculés côté serveur ; seuls les articles exclus sont envoyés.
 */
const GestionArticleMasse: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const {
    apercuData, apercuFamilleData, apercuFournisseurData, apercuPartageData, apercuValeursData,
    total, loading, applying, error, successMessage, avertissementCmup,
  } =
    useSelector((state: RootState) => state.ArticleSlice.masse);
  const selectedDepartement = useSelector((state: RootState) => state.AppSlice.selectedDepartement);
  const isAllMode = useSelector((state: RootState) => state.AppSlice.isAllMode);
  const allowedDepartements = useSelector((state: RootState) => state.ScopeSlice.allowedDepartements);
  const tousDepartements = useSelector((state: RootState) => state.DepartementSlice.departements);
  // Prix d'un département / copie des prix département de la famille : droit 0154 (refusé par l'API sinon)
  const peutModifierPrixDept = usePermission(PERMISSION_PRIX_DEPT.UPDATE);
  // Prix de revient (ajustement ou valeur de famille) : droit 0156 (refusé par l'API sinon)
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);

  useEffect(() => {
    dispatch(fetchAllowedDepartements());
    dispatch(fetchDepartements());
  }, [dispatch]);

  // Données communes (prix par défaut, famille, partage, valeurs de famille) : réservées
  // aux utilisateurs sans département interdit — même règle que l'API
  // (ScopeService.exigerAccesTousDepartements). Sinon : prix des départements autorisés.
  const communAutorise = isAllMode
    || (tousDepartements.length > 0 && allowedDepartements.length === tousDepartements.length);

  const [action, setAction] = useState<Action>('PRIX');

  // ── Cible ──
  const [famille, setFamille] = useState<FamilleData | null>(null);
  const [sansFamille, setSansFamille] = useState(false);
  const [sousFamilles, setSousFamilles] = useState(true);
  const [familleKey, setFamilleKey] = useState(0);

  // ── Action « prix » ──
  const [niveau, setNiveau] = useState<NiveauPrix>(NiveauPrix.DEFAUT);
  const [departement, setDepartement] = useState<Departement | null>(selectedDepartement ?? null);
  const [champ, setChamp] = useState<ChampPrix>(ChampPrix.PRIX_V);
  const [mode, setMode] = useState<ModeAjustement>(ModeAjustement.POURCENT);
  const [valeur, setValeur] = useState<string>('');
  const [arrondi, setArrondi] = useState<number | ''>('');

  // ── Action « famille » ──
  const [nouvelleFamille, setNouvelleFamille] = useState<FamilleData | null>(null);
  const [retirerFamille, setRetirerFamille] = useState(false);
  const [nouvelleFamilleKey, setNouvelleFamilleKey] = useState(0);

  // ── Action « fournisseur » (0143) ──
  const [nouveauFournisseur, setNouveauFournisseur] = useState<Tiers | null>(null);
  const [retirerFournisseur, setRetirerFournisseur] = useState(false);
  const [nouveauFournisseurKey, setNouveauFournisseurKey] = useState(0);

  // ── Action « partage » ──
  const [modePartage, setModePartage] = useState<ModePartage>(ModePartage.AJOUTER);
  const [departementsPartage, setDepartementsPartage] = useState<number[]>([]);

  // ── Action « valeurs de la famille » (lot 3) ──
  const [champsDefaut, setChampsDefaut] = useState<ChampDefautFamille[]>([]);
  const [defautOpen, setDefautOpen] = useState(false);

  // ── Aperçu : paramètres figés au clic « Calculer », pagination, exclusions ──
  const [apercuParams, setApercuParams] = useState<{ action: Action; params: ParamsAction } | null>(null);
  const [page, setPage] = useState({ limit: 10, offset: 0 });
  const [exclus, setExclus] = useState<Set<number>>(new Set());

  // Utilisateur limité : uniquement « Ajuster les prix » au niveau d'un département autorisé
  useEffect(() => {
    if (communAutorise) return;
    setAction('PRIX');
    setNiveau(NiveauPrix.DEPARTEMENT);
  }, [communAutorise]);

  const familleSourceId = sansFamille ? SANS_FAMILLE : famille?.id;

  const prixParams = useMemo<ArticleMassePrixParams | null>(() => {
    if (familleSourceId == null || valeur === '' || !Number.isFinite(Number(valeur))) return null;
    if (niveau === NiveauPrix.DEPARTEMENT && !departement?.id) return null;
    return {
      famille_id: familleSourceId,
      sous_familles: sousFamilles,
      departement_cible_id: niveau === NiveauPrix.DEPARTEMENT ? departement!.id : null,
      niveau, champ, mode,
      valeur: Number(valeur),
      arrondi: champ === ChampPrix.REMISE || arrondi === '' ? null : arrondi,
    };
  }, [familleSourceId, sousFamilles, niveau, departement, champ, mode, valeur, arrondi]);

  const familleParams = useMemo<ArticleMasseFamilleParams | null>(() => {
    const destId = retirerFamille ? SANS_FAMILLE : nouvelleFamille?.id;
    if (familleSourceId == null || destId == null) return null;
    return { famille_id: familleSourceId, sous_familles: sousFamilles, nouvelle_famille_id: destId };
  }, [familleSourceId, sousFamilles, retirerFamille, nouvelleFamille]);

  const fournisseurParams = useMemo<ArticleMasseFournisseurParams | null>(() => {
    const destId = retirerFournisseur ? SANS_FOURNISSEUR : nouveauFournisseur?.id;
    if (familleSourceId == null || destId == null) return null;
    return { famille_id: familleSourceId, sous_familles: sousFamilles, nouveau_fournisseur_id: destId };
  }, [familleSourceId, sousFamilles, retirerFournisseur, nouveauFournisseur]);

  const partageParams = useMemo<ArticleMassePartageParams | null>(() => {
    if (familleSourceId == null) return null;
    if (modePartage !== ModePartage.TOUS && departementsPartage.length === 0) return null;
    return {
      famille_id: familleSourceId, sous_familles: sousFamilles,
      mode_partage: modePartage,
      departements: modePartage === ModePartage.TOUS ? [] : departementsPartage,
    };
  }, [familleSourceId, sousFamilles, modePartage, departementsPartage]);

  const valeursParams = useMemo<ArticleMasseValeursParams | null>(() => {
    // « Sans famille » n'a pas de valeurs par défaut
    if (sansFamille || !famille?.id || champsDefaut.length === 0) return null;
    return { famille_id: famille.id, sous_familles: sousFamilles, champs: champsDefaut };
  }, [sansFamille, famille, sousFamilles, champsDefaut]);

  const formParams: ParamsAction | null =
    action === 'PRIX' ? prixParams
    : action === 'FAMILLE' ? familleParams
    : action === 'FOURNISSEUR' ? fournisseurParams
    : action === 'PARTAGE' ? partageParams
    : valeursParams;

  // Toute modification du formulaire invalide l'aperçu affiché
  useEffect(() => {
    setApercuParams(null);
    setExclus(new Set());
    dispatch(clearApercuMasse());
  }, [formParams, dispatch]);

  useEffect(() => {
    if (!apercuParams) return;
    const request =
      apercuParams.action === 'PRIX'
        ? dispatch(fetchApercuPrixMasse({ ...(apercuParams.params as ArticleMassePrixParams), ...page }))
      : apercuParams.action === 'FAMILLE'
        ? dispatch(fetchApercuFamilleMasse({ ...(apercuParams.params as ArticleMasseFamilleParams), ...page }))
      : apercuParams.action === 'FOURNISSEUR'
        ? dispatch(fetchApercuFournisseurMasse({ ...(apercuParams.params as ArticleMasseFournisseurParams), ...page }))
      : apercuParams.action === 'PARTAGE'
        ? dispatch(fetchApercuPartageMasse({ ...(apercuParams.params as ArticleMassePartageParams), ...page }))
        : dispatch(fetchApercuValeursMasse({ ...(apercuParams.params as ArticleMasseValeursParams), ...page }));
    return () => request.abort();
  }, [apercuParams, page, dispatch]);

  useEffect(() => {
    if (error) { showSnackbar(error, 'error'); dispatch(clearMessagesMasse()); }
    if (successMessage) {
      showSnackbar(successMessage, 'success');
      dispatch(clearMessagesMasse());
      // Pas de rechargement : l'aperçu recalculerait l'action sur les données déjà modifiées
      setApercuParams(null);
      setExclus(new Set());
      dispatch(clearApercuMasse());
    }
  }, [error, successMessage, showSnackbar, dispatch]);

  // Nettoyage au démontage uniquement
  useEffect(() => () => { dispatch(clearApercuMasse()); dispatch(clearMessagesMasse()); }, [dispatch]);

  const calculer = () => {
    if (!formParams) return;
    dispatch(clearMessagesMasse());
    setExclus(new Set());
    setPage((p) => ({ ...p, offset: 0 }));
    setApercuParams({ action, params: formParams });
  };

  const nbAppliques = total - exclus.size;

  const appliquer = async () => {
    if (!apercuParams || nbAppliques <= 0) return;
    const exclusIds = Array.from(exclus);
    dispatch(clearMessagesMasse());
    if (apercuParams.action === 'PRIX') {
      const cible = niveau === NiveauPrix.DEPARTEMENT ? ` du département « ${departement?.nom} »` : ' par défaut';
      if (!window.confirm(`Modifier le ${CHAMP_LABEL[champ].toLowerCase()}${cible} de ${nbAppliques} article(s) ?`)) return;
      await dispatch(appliquerPrixMasse({ ...(apercuParams.params as ArticleMassePrixParams), exclus: exclusIds }));
    } else if (apercuParams.action === 'FAMILLE') {
      const dest = retirerFamille ? 'sans famille' : `dans la famille « ${nouvelleFamille?.nom} »`;
      if (!window.confirm(`Placer ${nbAppliques} article(s) ${dest} ?`)) return;
      await dispatch(appliquerFamilleMasse({ ...(apercuParams.params as ArticleMasseFamilleParams), exclus: exclusIds }));
    } else if (apercuParams.action === 'FOURNISSEUR') {
      const dest = retirerFournisseur
        ? `${labels.retirerFournisseur} de`
        : `${labels.affecterFournisseur} « ${nouveauFournisseur ? [nouveauFournisseur.nom, nouveauFournisseur.prenom].filter(Boolean).join(' ') : ''} » à`;
      if (!window.confirm(`${dest} ${nbAppliques} article(s) ?`)) return;
      await dispatch(appliquerFournisseurMasse({ ...(apercuParams.params as ArticleMasseFournisseurParams), exclus: exclusIds }));
    } else if (apercuParams.action === 'PARTAGE') {
      if (!window.confirm(`${MODE_PARTAGE_LABEL[modePartage]} pour ${nbAppliques} article(s) ?`)) return;
      await dispatch(appliquerPartageMasse({ ...(apercuParams.params as ArticleMassePartageParams), exclus: exclusIds }));
    } else {
      if (!window.confirm(`Appliquer les valeurs de la famille « ${famille?.nom} » à ${nbAppliques} article(s) ?`)) return;
      await dispatch(appliquerValeursMasse({ ...(apercuParams.params as ArticleMasseValeursParams), exclus: exclusIds }));
    }
  };

  const toggleExclu = (id: number) =>
    setExclus((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const colonneInclure = {
    key: 'article_id' as const, label: 'Inclure', minWidth: 70,
    render: (row: { article_id: number }) => (
      <Checkbox size="small" checked={!exclus.has(row.article_id)} onChange={() => toggleExclu(row.article_id)} />
    ),
  };

  const headersPrix: TableHeader<ArticleMasseApercuLigne>[] = [
    colonneInclure,
    { key: 'code', label: 'Code' },
    { key: 'designation', label: 'Désignation' },
    { key: 'famille_nom', label: 'Famille', hideBelow: 'md' },
    { key: 'avant', label: 'Avant', render: (row) => fmt(row.avant) },
    {
      key: 'apres', label: 'Après',
      render: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: row.invalide ? '#b42318' : '#101828' }}>
            {fmt(row.apres)}
          </Typography>
          {row.ligne_dept_existante === false && <Chip size="small" label="nouveau prix dépt." />}
        </Box>
      ),
    },
  ];

  const headersFamille: TableHeader<ArticleMasseFamilleLigne>[] = [
    colonneInclure,
    { key: 'code', label: 'Code' },
    { key: 'designation', label: 'Désignation' },
    { key: 'famille_nom', label: 'Famille actuelle', render: (row) => row.famille_nom || 'Sans famille' },
    {
      key: 'nouvelle_famille_nom', label: 'Nouvelle famille',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#101828' }}>
          {row.nouvelle_famille_nom || 'Sans famille'}
        </Typography>
      ),
    },
  ];

  const headersFournisseur: TableHeader<ArticleMasseFournisseurLigne>[] = [
    colonneInclure,
    { key: 'code', label: 'Code' },
    { key: 'designation', label: 'Désignation' },
    { key: 'famille_nom', label: 'Famille', hideBelow: 'md' },
    { key: 'fournisseur_nom', label: labels.fournisseurActuel, render: (row) => row.fournisseur_nom || labels.sansFournisseur },
    {
      key: 'nouveau_fournisseur_nom', label: labels.nouveauFournisseur,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#101828' }}>
          {row.nouveau_fournisseur_nom || labels.sansFournisseur}
        </Typography>
      ),
    },
  ];

  const headersPartage: TableHeader<ArticleMassePartageLigne>[] = [
    colonneInclure,
    { key: 'code', label: 'Code' },
    { key: 'designation', label: 'Désignation' },
    { key: 'famille_nom', label: 'Famille', hideBelow: 'md' },
    { key: 'avant_noms', label: 'Partage actuel', render: (row) => row.avant_noms || 'Tous' },
    {
      key: 'apres_noms', label: 'Nouveau partage',
      render: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: row.invalide ? '#b42318' : row.change ? '#101828' : '#667085' }}
          >
            {row.apres_noms || 'Tous'}
          </Typography>
          {!row.change && !row.invalide && <Chip size="small" label="inchangé" />}
        </Box>
      ),
    },
  ];

  const headersValeurs: TableHeader<ArticleMasseValeursLigne>[] = [
    colonneInclure,
    { key: 'code', label: 'Code' },
    { key: 'designation', label: 'Désignation' },
    { key: 'famille_nom', label: 'Famille', hideBelow: 'md' },
    {
      key: 'modifications', label: 'Modifications',
      render: (row) => row.change
        ? <Typography variant="body2" sx={{ color: '#101828' }}>{row.modifications}</Typography>
        : <Chip size="small" label="déjà conforme" />,
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Actions par famille"
        subtitle="Modifier en masse les articles d'une famille"
        icon={<AutoFixHighIcon />}
        onBack={() => navigate('/article')}
        backLabel="Articles"
      />

      <AppSurface sx={{ mb: 2 }}>
        <ToggleButtonGroup
          exclusive size="small" color="primary" value={action} sx={{ mb: 2 }}
          onChange={(_, v: Action | null) => v && setAction(v)}
        >
          <ToggleButton value="PRIX">Ajuster les prix</ToggleButton>
          <ToggleButton value="FAMILLE" disabled={!communAutorise}>Changer de famille</ToggleButton>
          <ToggleButton value="FOURNISSEUR" disabled={!communAutorise}>{labels.fournisseur}</ToggleButton>
          <ToggleButton value="PARTAGE" disabled={!communAutorise}>Partage par département</ToggleButton>
          <ToggleButton value="VALEURS" disabled={!communAutorise}>Valeurs de la famille</ToggleButton>
        </ToggleButtonGroup>
        {!communAutorise && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Certains départements vous sont interdits : seuls les prix de vos départements peuvent être ajustés.
            Les autres actions modifient des données communes à tous les départements.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ color: '#667085', mb: 1 }}>
          {action === 'FAMILLE' ? 'Articles à déplacer' : 'Cible'}
        </Typography>
        <AppFilterGrid>
          <FamilleAutoC
            key={`famille-${familleKey}`}
            slot="main"
            type={GeneriqueDataType.FAMILLE_ARTICLE}
            label={action === 'FAMILLE' ? 'Famille actuelle' : 'Famille'}
            selectedFamille={famille}
            onFamilleChange={setFamille}
            disabled={sansFamille}
          />
          <FormControlLabel
            label="Sans famille"
            control={
              <Checkbox
                size="small"
                checked={sansFamille}
                onChange={(e) => { setSansFamille(e.target.checked); setFamille(null); setFamilleKey((k) => k + 1); }}
              />
            }
          />
          <FormControlLabel
            label="Inclure les sous-familles"
            control={<Switch size="small" checked={sousFamilles} disabled={sansFamille} onChange={(e) => setSousFamilles(e.target.checked)} />}
          />
          {famille?.id && !sansFamille && communAutorise && (
            <Button variant="outlined" size="small" onClick={() => setDefautOpen(true)}>
              Valeurs par défaut de la famille…
            </Button>
          )}
        </AppFilterGrid>

        <Typography variant="subtitle2" sx={{ color: '#667085', mt: 2, mb: 1 }}>
          {action === 'FAMILLE' ? 'Destination'
            : action === 'FOURNISSEUR' ? labels.fournisseurAAffecter
            : action === 'PARTAGE' ? 'Partage'
            : action === 'VALEURS' ? 'Valeurs à appliquer'
            : 'Ajustement'}
        </Typography>
        <AppFilterGrid>
          {action === 'VALEURS' ? (
            <FormControl fullWidth size="small" disabled={sansFamille}>
              <InputLabel shrink>Valeurs de la famille</InputLabel>
              <Select<ChampDefautFamille[]>
                multiple displayEmpty notched label="Valeurs de la famille"
                value={champsDefaut}
                onChange={(e) => {
                  const v = e.target.value;
                  setChampsDefaut((typeof v === 'string' ? v.split(',') : v) as ChampDefautFamille[]);
                }}
                renderValue={(sel) =>
                  sel.length === 0
                    ? (sansFamille ? '« Sans famille » n\'a pas de valeurs' : 'Choisir…')
                    : sel.map((c) => CHAMP_DEFAUT_LABEL[c]).join(', ')}
              >
                {Object.values(ChampDefautFamille).map((c) => (
                  <MenuItem
                    key={c} value={c}
                    disabled={(c === ChampDefautFamille.PRIX_DEPARTEMENTS && !peutModifierPrixDept)
                      || (c === ChampDefautFamille.PRIX_R && !voitCout)}
                  >
                    <Checkbox size="small" checked={champsDefaut.includes(c)} />
                    {CHAMP_DEFAUT_LABEL[c]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : action === 'FOURNISSEUR' ? (
            <>
              <FournisseurAutoC
                key={`nouveau-fournisseur-${nouveauFournisseurKey}`}
                label={labels.nouveauFournisseur}
                selectedFournisseur={nouveauFournisseur}
                onFournisseurChange={setNouveauFournisseur}
                disabled={retirerFournisseur}
              />
              <FormControlLabel
                label={`${labels.retirerFournisseur} (${labels.sansFournisseur.toLowerCase()})`}
                control={
                  <Checkbox
                    size="small"
                    checked={retirerFournisseur}
                    onChange={(e) => {
                      setRetirerFournisseur(e.target.checked);
                      setNouveauFournisseur(null);
                      setNouveauFournisseurKey((k) => k + 1);
                    }}
                  />
                }
              />
            </>
          ) : action === 'PARTAGE' ? (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select label="Mode" value={modePartage} onChange={(e) => setModePartage(e.target.value as ModePartage)}>
                  {Object.values(ModePartage).map((m) => <MenuItem key={m} value={m}>{MODE_PARTAGE_LABEL[m]}</MenuItem>)}
                </Select>
              </FormControl>
              <SelectDepartementsPartage
                value={modePartage === ModePartage.TOUS ? [] : departementsPartage}
                onChange={setDepartementsPartage}
                disabled={modePartage === ModePartage.TOUS}
                emptyLabel={modePartage === ModePartage.TOUS ? 'Tous les départements' : 'Choisir…'}
                helperText=""   // aide affichée sous la grille : un texte sous le champ le décale (grille centrée)
              />
            </>
          ) : action === 'PRIX' ? (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Niveau</InputLabel>
                <Select label="Niveau" value={niveau} onChange={(e) => setNiveau(e.target.value as NiveauPrix)}>
                  <MenuItem value={NiveauPrix.DEFAUT} disabled={!communAutorise}>Prix par défaut</MenuItem>
                  <MenuItem value={NiveauPrix.DEPARTEMENT} disabled={!peutModifierPrixDept}>Prix d'un département</MenuItem>
                </Select>
              </FormControl>
              {niveau === NiveauPrix.DEPARTEMENT && (
                <SelectDepartement selectedDepartement={departement} onDepartementChange={setDepartement} />
              )}
              <FormControl fullWidth size="small">
                <InputLabel>Champ</InputLabel>
                <Select label="Champ" value={champ} onChange={(e) => setChamp(e.target.value as ChampPrix)}>
                  {Object.values(ChampPrix).map((c) => (
                    <MenuItem key={c} value={c} disabled={c === ChampPrix.PRIX_R && !voitCout}>{CHAMP_LABEL[c]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select label="Mode" value={mode} onChange={(e) => setMode(e.target.value as ModeAjustement)}>
                  {Object.values(ModeAjustement).map((m) => <MenuItem key={m} value={m}>{MODE_LABEL[m]}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                size="small" fullWidth type="number"
                label={mode === ModeAjustement.FIXE ? 'Nouvelle valeur' : 'Ajustement (négatif = baisse)'}
                value={valeur}
                onChange={(e) => setValeur(e.target.value)}
              />
              {champ !== ChampPrix.REMISE && (
                <FormControl fullWidth size="small">
                  <InputLabel>Arrondi</InputLabel>
                  <Select label="Arrondi" value={arrondi} onChange={(e) => setArrondi(e.target.value as number | '')}>
                    <MenuItem value="">Aucun</MenuItem>
                    {ARRONDIS.map((a) => <MenuItem key={a} value={a}>Au multiple de {a}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </>
          ) : (
            <>
              {/* slot "parent" : options d'autocomplete distinctes de la famille source */}
              <FamilleAutoC
                key={`nouvelle-famille-${nouvelleFamilleKey}`}
                slot="parent"
                type={GeneriqueDataType.FAMILLE_ARTICLE}
                label="Nouvelle famille"
                selectedFamille={nouvelleFamille}
                onFamilleChange={setNouvelleFamille}
                disabled={retirerFamille}
              />
              <FormControlLabel
                label="Retirer la famille (sans famille)"
                control={
                  <Checkbox
                    size="small"
                    checked={retirerFamille}
                    onChange={(e) => {
                      setRetirerFamille(e.target.checked);
                      setNouvelleFamille(null);
                      setNouvelleFamilleKey((k) => k + 1);
                    }}
                  />
                }
              />
            </>
          )}
          <Button variant="contained" disabled={!formParams || loading} onClick={calculer}>
            Calculer l'aperçu
          </Button>
        </AppFilterGrid>
        {action === 'PARTAGE' && (modePartage === ModePartage.AJOUTER || modePartage === ModePartage.RETIRER) && (
          <Typography variant="caption" sx={{ color: '#667085', display: 'block', px: 2 }}>
            {modePartage === ModePartage.AJOUTER
              ? 'Un article déjà partagé avec tous les départements reste inchangé.'
              : 'Un article partagé avec tous reste partagé avec tous les autres départements.'}
          </Typography>
        )}
      </AppSurface>

      {(action === 'PRIX' || action === 'VALEURS') && avertissementCmup && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Le CMUP est actif à ce niveau : à la prochaine réception d'achat, le prix de revient ajusté servira de base
          à la moyenne pondérée (et sera remplacé par le coût d'achat si le stock est nul ou négatif).
        </Alert>
      )}

      {apercuParams && (
        <AppSurface padding={0}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} p={2}>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              {total} article(s) ciblé(s){exclus.size > 0 ? ` — ${exclus.size} exclu(s)` : ''}
            </Typography>
            <Button
              variant="contained" color="primary"
              disabled={applying || loading || nbAppliques <= 0}
              onClick={appliquer}
            >
              Appliquer à {nbAppliques} article(s)
            </Button>
          </Box>
          {apercuParams.action === 'PRIX' ? (
            <MyTable
              headers={headersPrix}
              data={apercuData}
              totalRows={total}
              loading={loading}
              defaultPageSize={page.limit}
              onPageChange={(p, size) => setPage({ limit: size, offset: p * size })}
              fieldLabels={getLabels('ArticleMasseApercuLigne')}
            />
          ) : apercuParams.action === 'FAMILLE' ? (
            <MyTable
              headers={headersFamille}
              data={apercuFamilleData}
              totalRows={total}
              loading={loading}
              defaultPageSize={page.limit}
              onPageChange={(p, size) => setPage({ limit: size, offset: p * size })}
              fieldLabels={getLabels('ArticleMasseFamilleLigne')}
            />
          ) : apercuParams.action === 'FOURNISSEUR' ? (
            <MyTable
              headers={headersFournisseur}
              data={apercuFournisseurData}
              totalRows={total}
              loading={loading}
              defaultPageSize={page.limit}
              onPageChange={(p, size) => setPage({ limit: size, offset: p * size })}
              fieldLabels={getLabels('ArticleMasseFournisseurLigne')}
            />
          ) : apercuParams.action === 'PARTAGE' ? (
            <MyTable
              headers={headersPartage}
              data={apercuPartageData}
              totalRows={total}
              loading={loading}
              defaultPageSize={page.limit}
              onPageChange={(p, size) => setPage({ limit: size, offset: p * size })}
              fieldLabels={getLabels('ArticleMassePartageLigne')}
            />
          ) : (
            <MyTable
              headers={headersValeurs}
              data={apercuValeursData}
              totalRows={total}
              loading={loading}
              defaultPageSize={page.limit}
              onPageChange={(p, size) => setPage({ limit: size, offset: p * size })}
              fieldLabels={getLabels('ArticleMasseValeursLigne')}
            />
          )}
        </AppSurface>
      )}

      {famille?.id && !sansFamille && (
        <FamilleDefautDialog
          open={defautOpen}
          onClose={() => setDefautOpen(false)}
          familleId={famille.id}
          familleNom={famille.nom}
        />
      )}
    </AppPage>
  );
};

export default GestionArticleMasse;
